import { useMemo, useState, type ReactElement } from "react";
import { ArrowRight, X } from "lucide-react";
import { Button, IconButton, Stack, Text } from "@ux/primitives";
import { WorkspacePicker } from "@ux/compositions";
import { palette, radius, space } from "@ux/tokens";
import { useAssetAssignments } from "../asset-assignments/hooks";
import { formatAssetDisplay } from "../assets/display";
import { useAssetEntries } from "../assets/hooks";
import { describeApiError } from "../common/errors";
import { useReassignWorkspaceAssets } from "./hooks";
import type { ReassignWorkspaceAssetsResult, WorkspaceView } from "./types";

export interface ReassignAllAssetsDialogProps {
    /** Workspace whose open assets are being moved away. */
    readonly sourceWorkspace: WorkspaceView;
    readonly onClose: () => void;
}

/**
 * "Reassign All": picks a destination workspace (typeahead via
 * `WorkspacePicker`) and moves every open asset at `sourceWorkspace` to it
 * in one server call.
 *
 * Three states, all derived from the mutation rather than parallel flags:
 *   - **Choosing** (mutation idle/errored): pick target, confirm.
 *   - **Working**: controls disabled while the request is in flight.
 *   - **Done**: the server reports per-asset outcomes. The move is atomic
 *     per asset but not across assets, so a partial result is a normal
 *     outcome — failures are listed and "Retry remaining" re-issues the
 *     call, which only picks up assets still at the source.
 *
 * Mount only while open; the parent unmounting it is what resets state.
 */
export function ReassignAllAssetsDialog({
    sourceWorkspace,
    onClose,
}: ReassignAllAssetsDialogProps): ReactElement {
    const [target, setTarget] = useState<WorkspaceView | null>(null);
    const mutation = useReassignWorkspaceAssets();

    // Same query key as `WorkspaceAssetsPanel`, so this is a cache hit.
    const assignmentsQuery = useAssetAssignments({
        assetId: "",
        userId: "",
        workspaceId: sourceWorkspace.id,
        targetKind: "workspace",
        openOnly: true,
    });
    const assetCount = assignmentsQuery.data?.length ?? 0;

    const result = mutation.data ?? null;
    const isWorking = mutation.isPending;
    const pickedSource = target !== null && target.id === sourceWorkspace.id;
    const canSubmit =
        target !== null && !pickedSource && assetCount > 0 && !isWorking && !assignmentsQuery.isPending;

    function submit(): void {
        if (target === null || pickedSource) return;
        mutation.mutate({
            sourceWorkspaceId: sourceWorkspace.id,
            targetWorkspaceId: target.id,
        });
    }

    const needsAttention = result !== null && (result.failed > 0 || result.hasMore);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Reassign all assets"
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: space[4],
                zIndex: 50,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isWorking) onClose();
            }}
        >
            <div
                style={{
                    background: palette.canvas,
                    border: `1px solid ${palette.border}`,
                    borderRadius: radius.lg,
                    padding: space[5],
                    width: "min(520px, 100%)",
                    maxHeight: "min(80vh, 720px)",
                    overflowY: "auto",
                }}
            >
                <Stack gap={4}>
                    <Stack direction="row" align="center" justify="space-between" gap={2}>
                        <Stack gap={1}>
                            <Text role="heading">Reassign all assets</Text>
                            <Text role="muted">
                                From {sourceWorkspace.name} · {sourceWorkspace.type}
                            </Text>
                        </Stack>
                        <IconButton
                            icon={<X size={16} />}
                            label="Close"
                            tone="neutral"
                            onClick={onClose}
                            disabled={isWorking}
                        />
                    </Stack>

                    {result === null ? (
                        <ChoosingBody
                            assetCount={assetCount}
                            isCountPending={assignmentsQuery.isPending}
                            target={target}
                            onTargetChange={setTarget}
                            pickedSource={pickedSource}
                            isWorking={isWorking}
                            errorMessage={
                                mutation.isError ? describeApiError(mutation.error) : null
                            }
                        />
                    ) : (
                        <ResultBody result={result} targetName={target?.name ?? ""} />
                    )}

                    <Stack direction="row" gap={2} justify="flex-end">
                        {result === null ? (
                            <>
                                <Button variant="ghost" onClick={onClose} disabled={isWorking}>
                                    Cancel
                                </Button>
                                <Button variant="primary" onClick={submit} disabled={!canSubmit}>
                                    {isWorking
                                        ? "Reassigning…"
                                        : assetCount > 0
                                          ? `Reassign ${assetCount} ${assetCount === 1 ? "asset" : "assets"}`
                                          : "Reassign"}
                                </Button>
                            </>
                        ) : (
                            <>
                                {needsAttention ? (
                                    <Button
                                        variant="secondary"
                                        onClick={submit}
                                        disabled={isWorking || target === null}
                                    >
                                        {isWorking ? "Retrying…" : "Retry remaining"}
                                    </Button>
                                ) : null}
                                <Button variant="primary" onClick={onClose} disabled={isWorking}>
                                    Close
                                </Button>
                            </>
                        )}
                    </Stack>
                </Stack>
            </div>
        </div>
    );
}

interface ChoosingBodyProps {
    readonly assetCount: number;
    readonly isCountPending: boolean;
    readonly target: WorkspaceView | null;
    readonly onTargetChange: (workspace: WorkspaceView | null) => void;
    readonly pickedSource: boolean;
    readonly isWorking: boolean;
    readonly errorMessage: string | null;
}

function ChoosingBody({
    assetCount,
    isCountPending,
    target,
    onTargetChange,
    pickedSource,
    isWorking,
    errorMessage,
}: ChoosingBodyProps): ReactElement {
    return (
        <Stack gap={3}>
            <Text role="body">
                {isCountPending
                    ? "Counting assets…"
                    : assetCount === 0
                      ? "This workspace has no assets to reassign."
                      : `Every asset currently assigned here (${assetCount}) will be released and assigned to the workspace you choose.`}
            </Text>

            <Stack gap={2}>
                <Text role="eyebrow">Move to workspace</Text>
                <WorkspacePicker
                    value={target}
                    onChange={onTargetChange}
                    placeholder="Type a workspace name…"
                    disabled={isWorking}
                    invalid={pickedSource}
                />
            </Stack>

            {pickedSource ? (
                <Text role="muted" color={palette.danger}>
                    That is the workspace the assets are already at. Pick a different one.
                </Text>
            ) : null}
            {errorMessage !== null ? (
                <Text role="muted" color={palette.danger}>
                    {errorMessage}
                </Text>
            ) : null}
        </Stack>
    );
}

function ResultBody({
    result,
    targetName,
}: {
    readonly result: ReassignWorkspaceAssetsResult;
    readonly targetName: string;
}): ReactElement {
    const failures = useMemo(
        () => result.results.filter((r) => r.outcome === "failed"),
        [result.results],
    );
    const { map: assetIndex } = useAssetEntries(failures.map((f) => f.assetId));

    return (
        <Stack gap={3}>
            <Stack direction="row" align="center" gap={1}>
                <Text role="body">
                    Moved {result.moved} {result.moved === 1 ? "asset" : "assets"}
                </Text>
                {targetName !== "" ? (
                    <>
                        <ArrowRight size={14} style={{ opacity: 0.55 }} />
                        <Text role="body">{targetName}</Text>
                    </>
                ) : null}
            </Stack>

            {result.hasMore ? (
                <Text role="muted">
                    More assets remain at the source than one request handles. Use “Retry
                    remaining” to continue.
                </Text>
            ) : null}

            {failures.length > 0 ? (
                <Stack gap={2}>
                    <Text role="eyebrow">{failures.length} could not be moved</Text>
                    {failures.map((f) => (
                        <Stack
                            key={f.assetId}
                            gap={1}
                            style={{
                                padding: `${space[2]} ${space[3]}`,
                                background: palette.surface,
                                border: `1px solid ${palette.border}`,
                                borderRadius: 6,
                            }}
                        >
                            <Text role="body">
                                {formatAssetDisplay(assetIndex.get(f.assetId) ?? null, f.assetId)}
                            </Text>
                            <Text role="muted" color={palette.danger}>
                                {f.errorMessage ?? "Unknown error."}
                            </Text>
                        </Stack>
                    ))}
                </Stack>
            ) : null}
        </Stack>
    );
}
